import React, { createContext, useContext, useState, useRef, ReactNode } from "react";
import { DateType } from "../../../../../my-type";
import type { FlightSchedule, Luggage, Place, RegistLuggage, ServiceBasicFee, ServiceFee, Terminal, Vehicle } from "../../../../../my-type";
import { OrderType } from "../../../../../my-enum";
import { FormInstance } from "rsuite";

export enum LuggageVarAction {
  Increase,
  Decrease
}

export enum PassengerType {
  Adult,
  Baby
}

interface OrderCreationContextType {
  // Step state
  step: number;
  setStep: (step: number) => void;
  headerTitle: string;
  setHeaderTitle: (title: string) => void;

  // Order details
  orderType: OrderType;
  setOrderType: (type: OrderType) => void;
  selectedAdress: string | undefined;
  setSelectedAddress: (address: string | undefined) => void;
  selectedPlace: Place | undefined;
  setSelectedPlace: (place: Place | undefined) => void;
  selectedTerminal: Terminal | undefined;
  setSelectedTerminal: (terminal: Terminal | undefined) => void;
  viaPoints: string[];
  setViaPoints: (points: string[]) => void;
  segmentDistances: number[];
  setSegmentDistances: (distances: number[]) => void;

  // Time and date
  rideTime: Date;
  setRideTime: (time: Date) => void;
  rideDateType: DateType;
  setRideDateType: (type: DateType) => void;
  estArriveTime: Date | undefined;
  setEstArriveTime: (time: Date | undefined) => void;

  // Passengers
  adultCount: number;
  setAdultCount: (count: number) => void;
  babyCount: number;
  setBabyCount: (count: number) => void;

  // Luggage
  userRegistLuggages: RegistLuggage[];
  setUserRegistLuggages: (luggages: RegistLuggage[]) => void;
  luggageNote: string;
  setLuggageNote: (note: string) => void;

  // Vehicle
  userVehicle: Vehicle | undefined;
  setUserVehicle: (vehicle: Vehicle | undefined) => void;
  vehicleServiceBasicFee: ServiceBasicFee | undefined;
  setVehicleServiceBasicFee: (fee: ServiceBasicFee | undefined) => void;
  addServiceFee: number;
  setAddServiceFee: (fee: number) => void;

  // Flight
  flightSchedule: FlightSchedule | undefined;
  setFlightSchedule: (schedule: FlightSchedule | undefined) => void;
  flightTime: Date | undefined;
  setFlightTime: (time: Date | undefined) => void;
  flightNo: string;
  setFlightNo: (flightNo: string) => void;
  isRidetimeTooLateOrEarly: boolean;
  setIsRidetimeTooLateOrEarly: (value: boolean) => void;
  isRideTimeValid: boolean;
  setIsRideTimeValid: (value: boolean) => void;

  // Passenger info
  passengerName: string | undefined;
  setPassengerName: (name: string | undefined) => void;
  passengerPhone: string | undefined;
  setPassengerPhone: (phone: string | undefined) => void;
  passengerEmail: string | undefined;
  setPassengerEmail: (email: string | undefined) => void;
  passengerID: string | undefined;
  setPassengerID: (id: string | undefined) => void;
  passengerFormValue: any;
  setPassengerFormValue: (value: any) => void;

  // Special requests
  specialRequests: string[];
  setSpecialRequests: (requests: string[]) => void;

  // Meet and greet details
  meetAndGreetDetails: string;
  setMeetAndGreetDetails: (details: string) => void;

  // UI state
  isShowWarning: boolean;
  setIsShowWarning: (value: boolean) => void;
  isShowLoading: boolean;
  setIsShowLoading: (value: boolean) => void;
  openAirportSelect: boolean;
  setOpenAirportSelect: (value: boolean) => void;

  // Data
  luggageTypes: Luggage[];
  setLuggageTypes: (types: Luggage[]) => void;
  vehicleTypes: Vehicle[];
  setVehicleTypes: (types: Vehicle[]) => void;
  serviceFeeData: ServiceFee[];
  setServiceFeeData: (data: ServiceFee[]) => void;

  // Created order
  newCreatedOrderID: string | undefined;
  setNewCreatedOrderID: (id: string | undefined) => void;

  // Refs
  refFlightNo: React.RefObject<HTMLInputElement>;
  refFormPassenger: React.RefObject<FormInstance>;
  refLuggageNote: React.RefObject<HTMLTextAreaElement>;

  // Helper methods
  changeLuggageQuantity: (action: LuggageVarAction, varIndex: number) => void;
  changePassengerCount: (passengerType: PassengerType, varCount: number) => void;
  displayRegistedLuggage: (withHtml: boolean) => JSX.Element | string;
  getTotalFee: () => number;
}

const OrderCreationContext = createContext<OrderCreationContextType | undefined>(undefined);

export const useOrderCreation = () => {
  const context = useContext(OrderCreationContext);
  if (!context) {
    throw new Error("useOrderCreation must be used within OrderCreationProvider");
  }
  return context;
};

interface OrderCreationProviderProps {
  children: ReactNode;
}

export const OrderCreationProvider: React.FC<OrderCreationProviderProps> = ({ children }) => {
  // Step state
  const [step, setStep] = useState<number>(0);
  const [headerTitle, setHeaderTitle] = useState<string>('預約機場接送');

  // Order details
  const [orderType, setOrderType] = useState<OrderType>(OrderType.送機);
  const [selectedAdress, setSelectedAddress] = useState<string | undefined>(undefined);
  const [selectedPlace, setSelectedPlace] = useState<Place | undefined>(undefined);
  const [selectedTerminal, setSelectedTerminal] = useState<Terminal | undefined>(undefined);
  const [viaPoints, setViaPoints] = useState<string[]>([]);
  const [segmentDistances, setSegmentDistances] = useState<number[]>([]);

  // Time and date
  const [rideTime, setRideTime] = useState<Date>(() => {
    const now = new Date();
    now.setDate(now.getDate() + 2);
    return now;
  });
  const [rideDateType, setRideDateType] = useState<DateType>(DateType.Working);
  const [estArriveTime, setEstArriveTime] = useState<Date | undefined>();

  // Passengers
  const [adultCount, setAdultCount] = useState<number>(1);
  const [babyCount, setBabyCount] = useState<number>(0);

  // Luggage
  const [userRegistLuggages, setUserRegistLuggages] = useState<RegistLuggage[]>([]);
  const [luggageNote, setLuggageNote] = useState<string>('');

  // Vehicle
  const [userVehicle, setUserVehicle] = useState<Vehicle | undefined>(undefined);
  const [vehicleServiceBasicFee, setVehicleServiceBasicFee] = useState<ServiceBasicFee | undefined>(undefined);
  const [addServiceFee, setAddServiceFee] = useState<number>(0);

  // Flight
  const [flightSchedule, setFlightSchedule] = useState<FlightSchedule | undefined>(undefined);
  const [flightTime, setFlightTime] = useState<Date | undefined>();
  const [flightNo, setFlightNo] = useState<string>("");
  const [isRidetimeTooLateOrEarly, setIsRidetimeTooLateOrEarly] = useState<boolean>(false);
  const [isRideTimeValid, setIsRideTimeValid] = useState<boolean>(false);

  // Passenger info
  const [passengerName, setPassengerName] = useState<string | undefined>(undefined);
  const [passengerPhone, setPassengerPhone] = useState<string | undefined>(undefined);
  const [passengerEmail, setPassengerEmail] = useState<string | undefined>(undefined);
  const [passengerID, setPassengerID] = useState<string | undefined>(undefined);
  const [passengerFormValue, setPassengerFormValue] = useState<any>({
    passengerName: '',
    passengerPhone: '',
    passengerEmail: '',
  });

  // Special requests
  const [specialRequests, setSpecialRequests] = useState<string[]>([]);
  const [meetAndGreetDetails, setMeetAndGreetDetails] = useState<string>("");

  // UI state
  const [isShowWarning, setIsShowWarning] = useState<boolean>(false);
  const [isShowLoading, setIsShowLoading] = useState<boolean>(false);
  const [openAirportSelect, setOpenAirportSelect] = useState<boolean>(false);

  // Data
  const [luggageTypes, setLuggageTypes] = useState<Luggage[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<Vehicle[]>([]);
  const [serviceFeeData, setServiceFeeData] = useState<ServiceFee[]>([]);

  // Created order
  const [newCreatedOrderID, setNewCreatedOrderID] = useState<string | undefined>(undefined);

  // Refs
  const refFlightNo = useRef<HTMLInputElement>(null);
  const refFormPassenger = useRef<FormInstance>(null);
  const refLuggageNote = useRef<HTMLTextAreaElement>(null);

  // Helper methods
  const changeLuggageQuantity = (action: LuggageVarAction, varIndex: number) => {
    let copyOfUserRegistLuggages = userRegistLuggages.slice();

    if (action === LuggageVarAction.Increase) {
      copyOfUserRegistLuggages[varIndex].quantity += 1;
    } else {
      if (copyOfUserRegistLuggages[varIndex].quantity <= 1) {
        copyOfUserRegistLuggages[varIndex].quantity = 0;
      } else {
        copyOfUserRegistLuggages[varIndex].quantity -= 1;
      }
    }

    setUserRegistLuggages(copyOfUserRegistLuggages);
  };

  const changePassengerCount = (passengerType: PassengerType, varCount: number) => {
    if (passengerType === PassengerType.Adult) {
      if (varCount === 1) {
        setAdultCount(adultCount + 1);
      }

      if (varCount === -1) {
        if (adultCount > 2) {
          setAdultCount(adultCount - 1);
        } else {
          setAdultCount(1);
        }
      }
    } else {
      if (varCount === 1) {
        setBabyCount(babyCount + 1);
      }

      if (varCount === -1) {
        if (babyCount > 1) {
          setBabyCount(babyCount - 1);
        } else {
          setBabyCount(0);
        }
      }
    }
  };

  const displayRegistedLuggage = (withHtml: boolean): JSX.Element | string => {
    let filteredVarUnSort = userRegistLuggages.filter((element) => element.quantity > 0);
    let filteredVar = filteredVarUnSort.sort((a, b) => b.luggage.size - a.luggage.size);
    if (filteredVar.length > 0) {
      if (filteredVar.length === 1) {
        if (withHtml) {
          return (<span>{filteredVar[0].luggage.discription}{filteredVar[0].quantity}件</span>);
        } else {
          return (filteredVar[0].luggage.discription + filteredVar[0].quantity + "件");
        }
      } else {
        if (withHtml) {
          return (<span>{filteredVar[0].luggage.discription}{filteredVar[0].quantity}件,還有另外{filteredVar.length - 1}項</span>);
        } else {
          return (filteredVar[0].luggage.discription + filteredVar[0].quantity + "件,還有另外" + (filteredVar.length - 1) + "項");
        }
      }
    } else {
      if (withHtml) {
        return (<span>未填寫</span>)
      } else {
        return ("未填寫")
      }
    }
  };

  const getTotalFee = (): number => {
    if (!vehicleServiceBasicFee) return 0;

    let totalFee = 0;
    if (orderType === OrderType.送機) {
      totalFee += vehicleServiceBasicFee.price;
    } else {
      totalFee += vehicleServiceBasicFee.price + vehicleServiceBasicFee.pickUpPrice;
    }

    // Check if ride time between 23:00-07:00
    if (rideTime.getHours() < 7 || rideTime.getHours() >= 23) {
      totalFee += 200; // 加收夜間服務費
    }

    totalFee += addServiceFee;

    // Add extra fees based on segment distances
    // Only for segments that drop off at via points
    const filteredViaPoints = viaPoints.filter(point => point.trim() !== '');
    segmentDistances.forEach((distanceMeters, index) => {
      let shouldIncludeFee = false;

      if (orderType === OrderType.送機) {
        // For drop-off: include segments ending at via points (index < filteredViaPoints.length)
        shouldIncludeFee = index < filteredViaPoints.length;
      } else {
        // For pickup: include segments starting from via points (skip first segment from terminal)
        shouldIncludeFee = index > 0;
      }

      if (shouldIncludeFee) {
        const distanceKm = distanceMeters / 1000;
        if (distanceKm >= 5) {
          totalFee += 500;
        } else if (distanceKm >= 3) {
          totalFee += 200;
        }
      }
    });

    if (rideDateType === DateType.Holiday) {
      totalFee += vehicleServiceBasicFee.holidayPrice;
    } else if (rideDateType === DateType.LastDayOfVacation) {
      totalFee += vehicleServiceBasicFee.holidayPrice + 300;
    }

    // Add meet and greet service fee
    if (meetAndGreetDetails && meetAndGreetDetails.trim() !== '') {
      totalFee += 200;
    }

    return totalFee;
  };

  const value: OrderCreationContextType = {
    step,
    setStep,
    headerTitle,
    setHeaderTitle,
    orderType,
    setOrderType,
    selectedAdress,
    setSelectedAddress,
    selectedPlace,
    setSelectedPlace,
    selectedTerminal,
    setSelectedTerminal,
    viaPoints,
    setViaPoints,
    segmentDistances,
    setSegmentDistances,
    rideTime,
    setRideTime,
    rideDateType,
    setRideDateType,
    estArriveTime,
    setEstArriveTime,
    adultCount,
    setAdultCount,
    babyCount,
    setBabyCount,
    userRegistLuggages,
    setUserRegistLuggages,
    luggageNote,
    setLuggageNote,
    userVehicle,
    setUserVehicle,
    vehicleServiceBasicFee,
    setVehicleServiceBasicFee,
    addServiceFee,
    setAddServiceFee,
    flightSchedule,
    setFlightSchedule,
    flightTime,
    setFlightTime,
    flightNo,
    setFlightNo,
    isRidetimeTooLateOrEarly,
    setIsRidetimeTooLateOrEarly,
    isRideTimeValid,
    setIsRideTimeValid,
    passengerName,
    setPassengerName,
    passengerPhone,
    setPassengerPhone,
    passengerEmail,
    setPassengerEmail,
    passengerID,
    setPassengerID,
    passengerFormValue,
    setPassengerFormValue,
    specialRequests,
    setSpecialRequests,
    meetAndGreetDetails,
    setMeetAndGreetDetails,
    isShowWarning,
    setIsShowWarning,
    isShowLoading,
    setIsShowLoading,
    openAirportSelect,
    setOpenAirportSelect,
    luggageTypes,
    setLuggageTypes,
    vehicleTypes,
    setVehicleTypes,
    serviceFeeData,
    setServiceFeeData,
    newCreatedOrderID,
    setNewCreatedOrderID,
    refFlightNo,
    refFormPassenger,
    refLuggageNote,
    changeLuggageQuantity,
    changePassengerCount,
    displayRegistedLuggage,
    getTotalFee,
  };

  return (
    <OrderCreationContext.Provider value={value}>
      {children}
    </OrderCreationContext.Provider>
  );
};
